/*
 * sequence.js — process-sequence section module
 *
 * Ported from the prototype's seqScroll(). The mechanics are unchanged:
 *
 *   p   = -rect.top / (section.offsetHeight - innerHeight), clamped 0..1
 *   prog.transform = scaleX(p)
 *   idx = floor(p * frameCount), capped at frameCount - 1
 *
 * For the prototype's three frames that reproduces its 0.34 / 0.67 thresholds.
 * Frames and beats are indexed independently so a 4-beat / 3-image setup (the
 * schema allows it) still behaves.
 *
 * ADDED BY THE BRIEF — video. Where a beat has a video, the frame is scrubbed:
 * currentTime tracks scroll position across that frame's own slice of p,
 * instead of the still simply cross-fading. Frames fall back to the image
 * whenever no video is set, and the two can be mixed across the three slots.
 *
 * THEME EDITOR — every listener is torn down on shopify:section:unload.
 * Without that, each re-render leaves a scroll handler bound to detached DOM
 * and the section progressively stops responding.
 */

const instances = new Map();

class ProcessSequence {
  constructor(root) {
    this.root = root;
    this.frames = Array.from(root.querySelectorAll('.seq-frame'));
    this.beats = Array.from(root.querySelectorAll('.seq-beat'));
    this.steps = Array.from(root.querySelectorAll('.seq-steps b'));
    this.prog = root.querySelector('.seq-prog i');

    this.frameIndex = -1;
    this.beatIndex = -1;
    this.destroyed = false;

    // Videos cannot be seeked until metadata has arrived.
    this.ready = new WeakMap();

    this.onScroll = window.Bioverde.rafThrottle(() => this.update());
    this.onResize = window.Bioverde.rafThrottle(() => this.update());

    this.init();
  }

  init() {
    if (window.Bioverde.prefersReducedMotion()) {
      this.applyStatic();
      return;
    }

    this.prepareVideos();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.update();
  }

  /*
   * Reduced motion. The stylesheet unpins the section and stacks the beats;
   * all this needs to do is make sure nothing is left at opacity 0, so the
   * content stays reachable by ordinary scrolling with no animation.
   */
  applyStatic() {
    this.frames.forEach((frame) => frame.classList.add('on'));
    this.beats.forEach((beat) => beat.classList.add('on'));
    this.steps.forEach((step) => step.classList.add('on'));
    this.root.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
    });
  }

  prepareVideos() {
    this.root.querySelectorAll('video').forEach((video) => {
      // We own currentTime; the browser must not also be playing the file.
      video.pause();
      video.muted = true;
      video.loop = false;
      video.autoplay = false;
      video.playsInline = true;

      if (video.readyState >= 1) {
        this.ready.set(video, true);
      } else {
        video.addEventListener('loadedmetadata', () => this.ready.set(video, true), { once: true });
      }
    });
  }

  update() {
    if (this.destroyed) return;

    const rect = this.root.getBoundingClientRect();
    const total = this.root.offsetHeight - window.innerHeight;
    if (total <= 0) return;

    const p = Math.min(1, Math.max(0, -rect.top / total));

    if (this.prog) this.prog.style.transform = 'scaleX(' + p + ')';

    this.renderFrames(p);
    this.renderBeats(p);
  }

  renderFrames(p) {
    const n = this.frames.length;
    if (!n) return;

    const idx = Math.min(n - 1, Math.floor(p * n));

    if (idx !== this.frameIndex) {
      this.frameIndex = idx;
      this.frames.forEach((frame, i) => {
        const on = i === idx;
        frame.classList.toggle('on', on);
        const video = frame.querySelector('video');
        if (video && !on) video.pause();
      });
    }

    // Scrub the active frame's video, if it has one.
    const active = this.frames[idx];
    if (!active) return;
    const video = active.querySelector('video');
    if (!video || !this.ready.get(video) || !video.duration) return;

    const slice = 1 / n;
    const local = Math.min(1, Math.max(0, (p - idx * slice) / slice));
    const target = local * video.duration;

    // Sub-frame seeks just thrash the decoder for no visible gain.
    if (Math.abs(video.currentTime - target) > 1 / 30) {
      try {
        video.currentTime = target;
      } catch (e) {
        /* seek rejected while still buffering; the next scroll frame retries */
      }
    }
  }

  renderBeats(p) {
    const n = this.beats.length;
    if (!n) return;

    const idx = Math.min(n - 1, Math.floor(p * n));
    if (idx === this.beatIndex) return;
    this.beatIndex = idx;

    this.beats.forEach((beat, i) => beat.classList.toggle('on', i === idx));
    this.steps.forEach((step, i) => step.classList.toggle('on', i === idx));
  }

  destroy() {
    this.destroyed = true;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.root.querySelectorAll('video').forEach((video) => video.pause());
  }
}

/* ------------------------------------------------------------------
 * Section lifecycle
 * ------------------------------------------------------------------ */

const SELECTOR = '[data-section-type="process-sequence"]';

function mount(root) {
  if (!root || instances.has(root)) return;
  instances.set(root, new ProcessSequence(root));
}

function unmount(root) {
  const instance = instances.get(root);
  if (!instance) return;
  instance.destroy();
  instances.delete(root);
}

function mountAll(scope) {
  (scope || document).querySelectorAll(SELECTOR).forEach(mount);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll());
} else {
  mountAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.matches && event.target.matches(SELECTOR) ? event.target : event.target.querySelector(SELECTOR);
  if (root) mount(root);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.matches && event.target.matches(SELECTOR) ? event.target : event.target.querySelector(SELECTOR);
  if (root) unmount(root);
});

export { ProcessSequence, mount, unmount };
